import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL =
  process.env.REACT_APP_BASE_URL || "https://backend.mystore.education/api";

// Helper function to get token
const getToken = () => {
  try {
    // Check sessionStorage first

    // const storedUser = JSON.parse(localStorage.getItem("loggedUser"));
    const sessionData = localStorage.getItem("loggedUser");
    if (sessionData) {
      const parsed = JSON.parse(sessionData);
      if (parsed.token) return parsed.token;
      if (parsed.data?.token) return parsed.data.token;
      if (parsed.result?.token) return parsed.result.token;
      if (parsed.user?.token) return parsed.user.token;
    }

    // Check localStorage
    const localData = localStorage.getItem("user");
    if (localData) {
      const parsed = JSON.parse(localData);
      if (parsed.token) return parsed.token;
      if (parsed.data?.token) return parsed.data.token;
      if (parsed.result?.token) return parsed.result.token;
      if (parsed.user?.token) return parsed.user.token;
    }

    return null;
  } catch (error) {
    console.error("Error getting token:", error);
    return null;
  }
};

// Fetch sliders
export const fetchSliders = createAsyncThunk(
  "sliders/fetchSliders",
  async (page = 1) => {
    try {
      const token = getToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(
        `${BASE_URL}/dashboard/sliders?page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Sliders API Error:", error);
      throw error;
    }
  }
);

// Add slider
export const addSlider = createAsyncThunk(
  "sliders/addSlider",
  async (formData) => {
    try {
      const token = getToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/dashboard/sliders`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Add Slider API Error:", error);
      throw error;
    }
  }
);

// Update slider
export const updateSlider = createAsyncThunk(
  "sliders/updateSlider",
  async ({ id, data }) => {
    try {
      const token = getToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/dashboard/sliders/${id}/update`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Update Slider API Error:", error);
      throw error;
    }
  }
);

// Delete slider
export const deleteSlider = createAsyncThunk(
  "sliders/deleteSlider",
  async (id) => {
    try {
      const token = getToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.delete(
        `${BASE_URL}/dashboard/sliders/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Delete Slider API Error:", error);
      throw error;
    }
  }
);

// Get single slider
export const getSlider = createAsyncThunk("sliders/getSlider", async (id) => {
  try {
    const token = getToken();
    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await axios.get(`${BASE_URL}/dashboard/sliders/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Get Slider API Error:", error);
    throw error;
  }
});

const sliderSlice = createSlice({
  name: "sliders",
  initialState: {
    sliders: { data: [], meta: null },
    loading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch sliders
      .addCase(fetchSliders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSliders.fulfilled, (state, action) => {
        state.loading = false;
        state.sliders = action.payload;
        state.error = null;
      })
      .addCase(fetchSliders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Add slider
      .addCase(addSlider.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addSlider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || "تم إضافة الـ Slider بنجاح";
        state.error = null;
      })
      .addCase(addSlider.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Update slider
      .addCase(updateSlider.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSlider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || "تم تحديث الـ Slider بنجاح";
        state.error = null;
      })
      .addCase(updateSlider.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Delete slider
      .addCase(deleteSlider.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSlider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || "تم حذف الـ Slider بنجاح";
        state.error = null;
      })
      .addCase(deleteSlider.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Get single slider
      .addCase(getSlider.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSlider.fulfilled, (state, action) => {
        state.loading = false;
        state.slider = action.payload.data;
        state.error = null;
      })
      .addCase(getSlider.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { clearState } = sliderSlice.actions;
export default sliderSlice.reducer;
